import { useState } from 'react';
import { Trash2, RotateCcw, AlertTriangle, X } from 'lucide-react';
import { generateClient } from 'aws-amplify/api';
import { useSites } from '../../hooks/useSites';
import { useScans } from '../../hooks/useScans';
import { formatDate } from '../../utils/formatting';
import {
  RESTORE_SITE_FROM_TRASH,
  PERMANENT_DELETE_SITE,
  RESTORE_SCAN_FROM_TRASH,
  PERMANENT_DELETE_SCAN,
} from '../../graphql/mutations';
import { Site, Scan } from '../../types';

const client = generateClient();

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

function ConfirmDialog({ isOpen, title, message, onConfirm, onCancel, isLoading }: ConfirmDialogProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-card p-6 max-w-md w-full mx-4 shadow-xl">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-status-error/10 flex items-center justify-center">
            <AlertTriangle size={20} className="text-status-error" />
          </div>
          <div className="flex-1">
            <h3 className="text-heading-3 font-semibold mb-2">{title}</h3>
            <p className="text-body text-gray-medium mb-6">{message}</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={onCancel}
                disabled={isLoading}
                className="btn-secondary py-2 px-4"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                disabled={isLoading}
                className="bg-status-error text-white py-2 px-4 rounded-card font-medium hover:bg-status-error/90 transition-colors disabled:opacity-50"
              >
                {isLoading ? 'Deleting...' : 'Delete Forever'}
              </button>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-medium hover:text-dark-text"
          >
            <X size={20} />
          </button>
        </div>
      </div>
    </div>
  );
}

export function TrashView() {
  const [activeTab, setActiveTab] = useState<'sites' | 'scans'>('sites');
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [isRestoring, setIsRestoring] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ type: 'site' | 'scan'; item: Site | Scan } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { sites, isLoading: sitesLoading, refetch: refetchSites } = useSites({ includeTrash: true });
  const { scans, isLoading: scansLoading, refetch: refetchScans } = useScans(selectedSiteId || '', {
    includeTrash: true,
    enableSubscription: false,
  });

  const trashedSites = sites.filter(site => site.trashedAt);
  const trashedScans = scans.filter(scan => scan.trashedAt);

  const handleRestoreSite = async (siteId: string) => {
    setIsRestoring(siteId);
    try {
      await client.graphql({
        query: RESTORE_SITE_FROM_TRASH,
        variables: { siteId },
      });
      await refetchSites();
    } catch (err) {
      console.error('Error restoring site:', err);
    } finally {
      setIsRestoring(null);
    }
  };

  const handlePermanentDeleteSite = async (site: Site) => {
    setIsDeleting(true);
    try {
      await client.graphql({
        query: PERMANENT_DELETE_SITE,
        variables: { siteId: site.siteId },
      });
      await refetchSites();
      setConfirmDelete(null);
    } catch (err) {
      console.error('Error permanently deleting site:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRestoreScan = async (scan: Scan) => {
    setIsRestoring(scan.scanId);
    try {
      await client.graphql({
        query: RESTORE_SCAN_FROM_TRASH,
        variables: { siteId: scan.siteId, scanId: scan.scanId },
      });
      await refetchScans();
    } catch (err) {
      console.error('Error restoring scan:', err);
    } finally {
      setIsRestoring(null);
    }
  };

  const handlePermanentDeleteScan = async (scan: Scan) => {
    setIsDeleting(true);
    try {
      await client.graphql({
        query: PERMANENT_DELETE_SCAN,
        variables: { siteId: scan.siteId, scanId: scan.scanId },
      });
      await refetchScans();
      setConfirmDelete(null);
    } catch (err) {
      console.error('Error permanently deleting scan:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleConfirmDelete = () => {
    if (!confirmDelete) return;
    if (confirmDelete.type === 'site') {
      handlePermanentDeleteSite(confirmDelete.item as Site);
    } else {
      handlePermanentDeleteScan(confirmDelete.item as Scan);
    }
  };

  return (
    <div className="py-8">
      <div className="container-content">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-heading-1 font-bold mb-2">Trash</h1>
          <p className="text-body text-gray-medium">
            View and manage archived sites and scans
          </p>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-light mb-6">
          <button
            onClick={() => setActiveTab('sites')}
            className={`px-6 py-3 text-body font-medium border-b-2 transition-colors
              ${activeTab === 'sites'
                ? 'border-coral text-coral'
                : 'border-transparent text-gray-medium hover:text-dark-text'
              }`}
          >
            Archived Sites ({trashedSites.length})
          </button>
          <button
            onClick={() => setActiveTab('scans')}
            className={`px-6 py-3 text-body font-medium border-b-2 transition-colors
              ${activeTab === 'scans'
                ? 'border-coral text-coral'
                : 'border-transparent text-gray-medium hover:text-dark-text'
              }`}
          >
            Archived Scans
          </button>
        </div>

        {/* Sites Tab Content */}
        {activeTab === 'sites' && (
          sitesLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin-slow w-8 h-8 border-2 border-coral border-t-transparent rounded-full" />
            </div>
          ) : trashedSites.length === 0 ? (
            <div className="card text-center py-12">
              <Trash2 size={48} className="mx-auto text-gray-medium mb-4" />
              <h2 className="text-heading-3 font-semibold mb-2">No Archived Sites</h2>
              <p className="text-body text-gray-medium">
                Sites you archive will appear here
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {trashedSites.map(site => (
                <div key={site.siteId} className="card">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-heading-3 font-semibold mb-1">{site.name}</h3>
                      <p className="text-body-sm text-gray-medium">{site.url}</p>
                      {site.trashedAt && (
                        <p className="text-caption text-gray-medium mt-2">
                          Archived on {formatDate(site.trashedAt)}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleRestoreSite(site.siteId)}
                        disabled={isRestoring === site.siteId}
                        className="btn-secondary flex items-center gap-2 py-2 px-4 disabled:opacity-50"
                      >
                        <RotateCcw size={16} className={isRestoring === site.siteId ? 'animate-spin' : ''} />
                        {isRestoring === site.siteId ? 'Restoring...' : 'Restore'}
                      </button>
                      <button
                        onClick={() => setConfirmDelete({ type: 'site', item: site })}
                        className="btn-ghost flex items-center gap-2 py-2 px-4 text-status-error hover:bg-status-error/10"
                      >
                        <AlertTriangle size={16} />
                        Delete Forever
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {/* Scans Tab Content */}
        {activeTab === 'scans' && (
          <div>
            {/* Site Selector */}
            <div className="mb-6">
              <label className="block text-body-sm font-medium text-gray-medium mb-2">
                Select a site to view its archived scans
              </label>
              <select
                value={selectedSiteId || ''}
                onChange={(e) => setSelectedSiteId(e.target.value || null)}
                className="w-full max-w-md px-4 py-2 border border-gray-light rounded-card text-body focus:outline-none focus:ring-2 focus:ring-coral/50"
              >
                <option value="">Choose a site...</option>
                {sites.map(site => (
                  <option key={site.siteId} value={site.siteId}>
                    {site.name} ({site.url})
                  </option>
                ))}
              </select>
            </div>

            {!selectedSiteId ? (
              <div className="card text-center py-12">
                <Trash2 size={48} className="mx-auto text-gray-medium mb-4" />
                <h2 className="text-heading-3 font-semibold mb-2">Select a Site</h2>
                <p className="text-body text-gray-medium">
                  Choose a site from the dropdown to view its archived scans
                </p>
              </div>
            ) : scansLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin-slow w-8 h-8 border-2 border-coral border-t-transparent rounded-full" />
              </div>
            ) : trashedScans.length === 0 ? (
              <div className="card text-center py-12">
                <Trash2 size={48} className="mx-auto text-gray-medium mb-4" />
                <h2 className="text-heading-3 font-semibold mb-2">No Archived Scans</h2>
                <p className="text-body text-gray-medium">
                  This site has no archived scans
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {trashedScans.map(scan => (
                  <div key={scan.scanId} className="card">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-heading-3 font-semibold mb-1">
                          Scan from {formatDate(scan.startedAt)}
                        </h3>
                        <div className="flex gap-4 text-body-sm text-gray-medium">
                          <span>Status: {scan.status}</span>
                          {scan.results?.overallScore !== undefined && (
                            <span>Score: {scan.results.overallScore}</span>
                          )}
                          {scan.progress?.pagesScanned !== undefined && (
                            <span>Pages: {scan.progress.pagesScanned}</span>
                          )}
                        </div>
                        {scan.trashedAt && (
                          <p className="text-caption text-gray-medium mt-2">
                            Archived on {formatDate(scan.trashedAt)}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleRestoreScan(scan)}
                          disabled={isRestoring === scan.scanId}
                          className="btn-secondary flex items-center gap-2 py-2 px-4 disabled:opacity-50"
                        >
                          <RotateCcw size={16} className={isRestoring === scan.scanId ? 'animate-spin' : ''} />
                          {isRestoring === scan.scanId ? 'Restoring...' : 'Restore'}
                        </button>
                        <button
                          onClick={() => setConfirmDelete({ type: 'scan', item: scan })}
                          className="btn-ghost flex items-center gap-2 py-2 px-4 text-status-error hover:bg-status-error/10"
                        >
                          <AlertTriangle size={16} />
                          Delete Forever
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={!!confirmDelete}
        title="Permanently Delete?"
        message={
          confirmDelete?.type === 'site'
            ? `This will permanently delete "${(confirmDelete.item as Site).name}" and all its associated scans. This action cannot be undone.`
            : `This will permanently delete this scan from ${formatDate((confirmDelete?.item as Scan)?.startedAt)}. This action cannot be undone.`
        }
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDelete(null)}
        isLoading={isDeleting}
      />
    </div>
  );
}
